// ════════════════════════════════════════════════════════════════════════════
//  ManDana — what Backspace un-does
//  ---------------------------------------------------------------------------
//  A writer who picked "To-do" from the "/" menu never typed "- [ ] ", and must
//  never have to un-type it. But the record IS markdown: the marker is six real
//  characters sitting in the source with no width, so a plain Backspace at the
//  start of the text deletes the trailing space, the line stops being a to-do,
//  and "- [ ]Buy milk" appears on screen as literal text. Four more presses to
//  chew through the rest. The shortcut was a shortcut going in and a raw string
//  coming out.
//
//  So Backspace deletes the FORMAT, not the syntax:
//    • at the start of a block's text  → the whole marker goes, in one press,
//      leaving a plain paragraph. A nested item outdents first and only sheds
//      its marker at the margin, so Backspace unwinds nesting the way Shift-Tab
//      does.
//    • against an emphasis marker      → the PAIR goes. Deleting one star of
//      "**bold**" leaves "**bold*", which the renderer reads as italic, and the
//      writer watches their word change shape while trying to erase it.
//  The invariant behind both: a marker is never left half-deleted, because a
//  half-deleted marker is exactly the moment the syntax becomes visible.
//
//  What this deliberately does NOT touch:
//    • the start of a plain paragraph — that Backspace joins to the line above,
//      as it always has, and md-markdown.jsx already owns it.
//    • links. "[text](url)" holds a target, not a decoration; dropping both
//      halves on one keystroke throws away a URL the writer can no longer see.
//      ⌘K edits a link; Backspace leaves it alone.
//
//  Pure string work — a source, a caret, and the source and caret they become —
//  for the same reason md-list.ts and md-link.ts are: these operations rewrite
//  the writer's text underneath their caret, and a contentEditable is the one
//  place in this app that cannot be unit-tested. Pinned in tests/unformat.test.js.
// ════════════════════════════════════════════════════════════════════════════

/** A rewritten source and where the caret goes in it. Mirrors MdListEdit. */
interface MdUnformatEdit {
  src: string;
  caret: number;
}

/** The leading whitespace of a line, and the block marker that follows it. */
interface MdBlockPrefix {
  pad: string;
  marker: string;
}

(function (g: any) {
  'use strict';

  /* The prefixes a block format is made of. This is the same grammar
     md-markdown.jsx draws with (mdLineKind) and applies with (mdApplyBlock) —
     it lives here now so the three cannot drift, and the editor reads it from
     the global like everything else. The to-do form comes before the plain
     bullet for the reason md-list.ts gives: "- [ ] " also starts with "- ", and
     the looser pattern would win and eat the checkbox. */
  const MD_BLOCK_RE = /^([ \t]*)(#{1,3} |- \[[ xX]\] |[-*] |\d+\. |> )?/;

  /* The inline runs, mirroring mdInline's regex in md-markdown.jsx — bold
     before italic, and links matched so they can be RECOGNISED and then left
     alone. Keep the two in step: a run this file cannot see is a pair the
     writer can still break in half. */
  const MD_INLINE_RE = /(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(`[^`\n]+`)|(\[[^\]\n]+\]\([^)\n]+\))/g;

  /** What block format this line wears, taken apart. Both halves may be ''. */
  function mdBlockPrefix(line: unknown): MdBlockPrefix {
    if (typeof line !== 'string') return { pad: '', marker: '' };
    const m = MD_BLOCK_RE.exec(line);
    return { pad: (m && m[1]) || '', marker: (m && m[2]) || '' };
  }

  /* Backspace, when it means "un-format" rather than "delete a character".

     Inputs
       src            the whole record source
       off            the collapsed caret, as a character offset into it
       firstLineTitle is line 0 the record's NAME? (title mode — see mdLineKind)

     Returns the rewritten source and caret, or null for "not mine": the caller
     then lets the ordinary deletion happen, which is most keystrokes. */
  function mdUnformat(src: unknown, off: unknown, firstLineTitle?: boolean): MdUnformatEdit | null {
    if (typeof src !== 'string' || typeof off !== 'number') return null;
    if (!isFinite(off) || off <= 0 || off > src.length) return null;

    const ls = src.lastIndexOf('\n', off - 1) + 1;
    let le = src.indexOf('\n', ls);
    if (le < 0) le = src.length;
    const line = src.slice(ls, le);

    /* In title mode line 0 is the record's name: block markers there are literal
       characters, drawn as themselves, so there is no format to un-do. Inline
       emphasis DOES render on the title, so the scan below still runs — it just
       reads the whole line, because none of it is a marker. */
    const titleLine = !!firstLineTitle && ls === 0;
    const { pad, marker } = titleLine ? { pad: '', marker: '' } : mdBlockPrefix(line);
    const contentStart = ls + pad.length + marker.length;

    /* ── the block half ──────────────────────────────────────────────────────
       Only exactly at the start of the text. One character to the left is
       inside the marker, where the caret cannot stand (md-markdown redirects
       it), and one to the right is a letter the writer means to delete. */
    if (marker && off === contentStart) {
      /* A nested item climbs before it un-formats. The whole subtree comes with
         it, exactly as Shift-Tab moves it — an item that left its children
         behind would silently stop being their parent. */
      const item = g.mdListParse ? g.mdListParse(line) : null;
      if (item && item.level > 0 && g.mdListOutdent) {
        const step = g.mdListOutdent(src, off) as MdUnformatEdit | null;
        if (step && step.src !== src) return step;
      }
      /* The pad stays. Only the marker was the format; the whitespace in front
         of it is text the writer put there, and on a heading or a quote — which
         have no levels to climb — it is all that was ever holding the indent. */
      const next = pad + line.slice(pad.length + marker.length);
      return { src: src.slice(0, ls) + next + src.slice(le), caret: ls + pad.length };
    }

    /* ── the inline half ─────────────────────────────────────────────────────
       Scanned over the CONTENT, never the whole line: "* a *b* c" is a bullet
       whose text reads "a b c", and a scan that started at the line would pair
       the bullet's own star with the italic's opener. */
    const content = line.slice(pad.length + marker.length);
    MD_INLINE_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = MD_INLINE_RE.exec(content))) {
      if (m[4]) continue;                       // a link: recognised, then left alone
      const text = m[0];
      const mlen = m[1] ? 2 : 1;
      const s = contentStart + m.index;
      const e = s + text.length;
      const inner = text.slice(mlen, text.length - mlen);
      /* Which side the caret is deleting into decides where it lands: erase the
         opener and you are at the front of the word, erase the closer and you
         are behind it. Either way both markers go and the word stays put. */
      let caret: number;
      if (off > s && off <= s + mlen) caret = s;
      else if (off > e - mlen && off <= e) caret = s + inner.length;
      else continue;
      return { src: src.slice(0, s) + inner + src.slice(e), caret };
    }

    return null;
  }

  g.MD_BLOCK_RE = MD_BLOCK_RE;
  g.mdBlockPrefix = mdBlockPrefix;
  g.mdUnformat = mdUnformat;

  // CommonJS escape hatch, following md-list.ts and md-link.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    (mod.exports as Record<string, unknown>).mdBlockPrefix = mdBlockPrefix;
    (mod.exports as Record<string, unknown>).mdUnformat = mdUnformat;
  }
})(typeof window !== 'undefined' ? window : globalThis);
